import type { ShopItem, VipStatus } from "../../types";
import { formatNumber, getOldPrice, pluralize } from "../../utils";
import CountdownTimer from "../CountdownTimer";
import styles from "./styles.module.css";

export type CardVariant = "redOffer" | "violetOffer" | "purple" | "orange" | "common";

type Props = {
  product: ShopItem;
  onChooseClick: () => void;
  variant?: CardVariant;
  expirationDate?: number;
  vipStatus?: VipStatus | null;
  hideLabel?: boolean;
};

const FREE_SPINS_FORMS = { one: "фриспин", few: "фриспина", many: "фриспинов" };

function getLabelText(variant: CardVariant, labelType: string | null): string {
  if (variant === "redOffer" || variant === "violetOffer") return "успей купить";
  if (labelType === "bestPrice") return "лучшая цена";
  return "хит продаж";
}

export default function ShopItemView({
  product,
  onChooseClick,
  variant = "common",
  expirationDate,
  vipStatus,
  hideLabel = false,
}: Props) {
  const isOffer = variant === "redOffer" || variant === "violetOffer";
  const hasLabel = variant !== "common" && !hideLabel;
  const freeSpins = product.slotFreeSpins;
  const labelText = getLabelText(variant, product.labelType);

  const isVipActive = !freeSpins && vipStatus?.isActive && product.vipResources.length > 0;
  const resources = isVipActive ? product.vipResources : product.resources;
  const resourceAmount = resources?.[0]?.amount ?? 0;
  const originalResourceAmount = product.resources?.[0]?.amount ?? 0;

  return (
    <div className={styles.wrapper} onClick={onChooseClick}>
      {hasLabel && (
        <div className={`${styles.labelTab} ${styles[variant]}`}>
          <span>{labelText}</span>
        </div>
      )}
      <div className={`${styles.card} ${styles[variant]} ${hasLabel ? styles.hasLabel : ""}`}>
        {isVipActive && product.purchaseBonusPercent && product.purchaseBonusPercent > 0 && (
          <div className={styles.vipBonusBadge}>
            <img src="/vip_icon_pill.svg" alt="VIP" width="16" height="16" />
            <span>+{product.purchaseBonusPercent}%</span>
          </div>
        )}
        <div className={styles.imageWrap}>
          <img src={product.image} alt={product.title} />
        </div>
        <div className={styles.middle}>
          <div className={styles.amountContainer}>
            {freeSpins ? (
              <>
                <div className={`${styles.amount} ${styles[variant]}`}>
                  {formatNumber(freeSpins.count)} {pluralize(freeSpins.count, FREE_SPINS_FORMS)}
                </div>
                <div className={styles.subAmount}>ставка {formatNumber(freeSpins.bet)}</div>
              </>
            ) : isVipActive ? (
              <>
                <div className={`${styles.amount} ${styles[variant]} ${styles.strikethrough}`}>{formatNumber(originalResourceAmount)}</div>
                <div className={`${styles.amount} ${styles[variant]} ${styles.vipAmount}`}>{formatNumber(resourceAmount)}</div>
              </>
            ) : (
              <div className={`${styles.amount} ${styles[variant]}`}>{formatNumber(resourceAmount)}</div>
            )}
          </div>
          {isOffer && expirationDate != null && (
            <div className={styles.timer}>
              <CountdownTimer targetEpochSeconds={expirationDate} />
            </div>
          )}
        </div>
        <button
          className={`${styles.buyButton} ${styles[variant]}`}
          onClick={(e) => {
            e.stopPropagation();
            onChooseClick();
          }}
        >
          {product.discount > 0 && (
            <div className={`${styles.discountBadge} ${styles[variant]}`}>
              <span>-{product.discount}%</span>
            </div>
          )}
          {product.discount > 0 && (
            <div className={styles.oldPrice}>
              {formatNumber(getOldPrice(product.price, product.discount))} ₽
            </div>
          )}
          <div className={styles.currentPrice}>{formatNumber(product.price)} ₽</div>
        </button>
      </div>
    </div>
  );
}
